// src/contexts/UserSettingsContext.js
import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from '../hooks/useAuth';
import userSettingsService from '../services/userSettings.service';

const UserSettingsContext = createContext();

export const useUserSettings = () => {
    const context = useContext(UserSettingsContext);
    if (!context) {
        throw new Error('useUserSettings must be used within UserSettingsProvider');
    }
    return context;
};

export const UserSettingsProvider = ({ children }) => {
    const { user } = useAuth();

    // Font Settings
    const [fontSize, setFontSize] = useState(() => {
        return localStorage.getItem('oakhr_fontSize') || 'medium';
    });

    // Notification Preferences
    const [notificationSettings, setNotificationSettings] = useState(() => {
        const saved = localStorage.getItem('oakhr_notificationSettings');
        return saved ? JSON.parse(saved) : {
            emailEnabled: true,
            inAppEnabled: true
        };
    });

    // Security Settings
    const [securitySettings, setSecuritySettings] = useState({
        twoFactorEnabled: false,
        securityQuestions: [],
        activeSessions: [],
        loginHistory: []
    });

    // Email Preferences
    const [emailPreferences, setEmailPreferences] = useState(() => {
        const saved = localStorage.getItem('oakhr_emailPreferences');
        return saved ? JSON.parse(saved) : {
            primaryEmail: user?.email || '',
            secondaryEmail: '',
            emailSignature: '',
            newsletter: true
        };
    });

    // Apply font size to root element
    useEffect(() => {
        const root = document.documentElement;
        root.classList.remove('text-small', 'text-medium', 'text-large');
        root.classList.add(`text-${fontSize}`);
        localStorage.setItem('oakhr_fontSize', fontSize);
    }, [fontSize]);

    // Save notification settings
    useEffect(() => {
        localStorage.setItem('oakhr_notificationSettings', JSON.stringify(notificationSettings));
    }, [notificationSettings]);

    // Save email preferences
    useEffect(() => {
        localStorage.setItem('oakhr_emailPreferences', JSON.stringify(emailPreferences));
    }, [emailPreferences]);

    // Update font size
    const updateFontSize = (size) => {
        setFontSize(size);
    };

    // Update notification settings
    const updateNotificationSetting = (key, _, value) => {
        setNotificationSettings(prev => ({
            ...prev,
            [key]: value
        }));
    };

    // Update email preferences
    const updateEmailPreferences = (updates) => {
        setEmailPreferences(prev => ({
            ...prev,
            ...updates
        }));
    };

    // Update security settings
    const updateSecuritySettings = (updates) => {
        setSecuritySettings(prev => ({
            ...prev,
            ...updates
        }));
    };

    // Load settings from backend (if API is available)
    useEffect(() => {
        if (user?.id) {
            loadUserSettings();
        }
    }, [user?.id]);

    const loadUserSettings = async () => {
        try {
            const data = await userSettingsService.getUserSettings();
            if (data.font_size) setFontSize(data.font_size);
            if (data.notification_settings) setNotificationSettings(data.notification_settings);
            if (data.email_preferences) setEmailPreferences(data.email_preferences);
            if (data.security_settings) setSecuritySettings(data.security_settings);
        } catch (error) {
            console.error('Error loading user settings:', error);
            // Keep using localStorage values as fallback
        }
    };

    const saveSettings = async (settingsData) => {
        try {
            const result = await userSettingsService.updateUserSettings(settingsData);
            console.log('Settings saved to backend:', result);
            return result;
        } catch (error) {
            console.error('Error saving settings:', error);
            throw error;
        }
    };

    const value = {
        // Font Settings
        fontSize,
        updateFontSize,

        // Notification Settings
        notificationSettings,
        updateNotificationSetting,

        // Email Preferences
        emailPreferences,
        updateEmailPreferences,

        // Security Settings
        securitySettings,
        updateSecuritySettings,

        // Actions
        saveSettings,
        loadUserSettings
    };

    return (
        <UserSettingsContext.Provider value={value}>
            {children}
        </UserSettingsContext.Provider>
    );
};

export default UserSettingsContext;
